
import React, { useState, useEffect, useRef } from 'react';
import { TypingMode, Language, SavedResult, TypingStats, HistoryEntry, UserSettings } from './types';
import { TypingEngine } from './components/TypingEngine';
import { HistoryView } from './components/HistoryView';
import { SettingsView } from './components/SettingsView';
import { LeaderboardView } from './components/LeaderboardView';
import { AuthModal } from './components/AuthModal';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { saveUserResult, getUserHistory, saveUserSettings, getUserSettings } from './services/dbService';
import { Settings, Keyboard, User, Clock, Hash, Zap, Target, Globe, Quote as QuoteIcon, List, EyeOff, Trophy, ChevronDown, Check } from 'lucide-react';

export const THEMES = {
  deep_green: { bg: '#0d1b1e', main: '#00ff88', text: '#d1d0c5', sub: '#2a4d4e' },
  gray: { bg: '#2b2b2b', main: '#ffffff', text: '#d1d1d1', sub: '#666666' },
  pink: { bg: '#2e1a22', main: '#ff69b4', text: '#f8f8f2', sub: '#633c4a' },
  dark: { bg: '#323437', main: '#e2b714', text: '#d1d0c5', sub: '#646669' },
  light: { bg: '#ffffff', main: '#000000', text: '#444444', sub: '#999999' },
  serika: { bg: '#323437', main: '#e2b714', text: '#d1d0c5', sub: '#646669' },
  carbon: { bg: '#292a2d', main: '#f5e132', text: '#e1e1e1', sub: '#616161' },
  monokai: { bg: '#272822', main: '#a6e22e', text: '#f8f8f2', sub: '#75715e' },
  nord: { bg: '#2e3440', main: '#88c0d0', text: '#d8dee9', sub: '#4c566a' },
  dracula: { bg: '#282a36', main: '#bd93f9', text: '#f8f8f2', sub: '#44475a' },
  solarized: { bg: '#002b36', main: '#268bd2', text: '#839496', sub: '#073642' },
  cyberpunk: { bg: '#2d1b33', main: '#00ff00', text: '#f0f', sub: '#5a2a6b' },
  botanical: { bg: '#1f2421', main: '#7b9e89', text: '#e9efec', sub: '#495149' },
  lavender: { bg: '#1a1a2e', main: '#a29bfe', text: '#e6e6fa', sub: '#483d8b' },
  espresso: { bg: '#121212', main: '#d2b48c', text: '#f5f5dc', sub: '#3e3e3e' },
  ocean: { bg: '#0f172a', main: '#38bdf8', text: '#f1f5f9', sub: '#334155' },
  sunset: { bg: '#2d1b1b', main: '#f97316', text: '#fef3c7', sub: '#7c2d12' },
  midnight: { bg: '#020617', main: '#6366f1', text: '#f8fafc', sub: '#1e293b' },
  retro: { bg: '#3c3836', main: '#d79921', text: '#ebdbb2', sub: '#665c54' },
  mint: { bg: '#0a1a12', main: '#a7f3d0', text: '#f0fdf4', sub: '#064e3b' }
};

const App: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [mode, setMode] = useState<TypingMode>(TypingMode.TIME);
  const [config, setConfig] = useState(30);
  const [language, setLanguage] = useState<Language>(Language.EN);
  const [view, setView] = useState<'test' | 'history' | 'settings' | 'leaderboard'>('test');
  const [showAuth, setShowAuth] = useState(false);
  const [results, setResults] = useState<SavedResult[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  
  const [settings, setSettings] = useState<UserSettings>({
    theme: 'deep_green',
    fontSize: 24,
    fontFamily: 'JetBrains Mono',
    focusMode: false,
    language: Language.EN
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const cloudSettings = await getUserSettings(u.uid);
        if (cloudSettings) setSettings(cloudSettings);
        const cloudHistory = await getUserHistory(u.uid);
        setResults(cloudHistory);
      }
    });
  }, []);

  useEffect(() => {
    const theme = THEMES[settings.theme as keyof typeof THEMES] || THEMES.deep_green;
    const root = document.documentElement;
    root.style.setProperty('--bg-color', theme.bg);
    root.style.setProperty('--main-color', theme.main);
    root.style.setProperty('--text-color', theme.text);
    root.style.setProperty('--sub-color', theme.sub);
    root.style.setProperty('--font-size', `${settings.fontSize}px`);
    root.style.setProperty('--font-family', `${settings.fontFamily}, monospace`);
  }, [settings]);

  const handleModeChange = (newMode: TypingMode) => {
    setMode(newMode);
    if (newMode === TypingMode.TIME) setConfig(30);
    else if (newMode === TypingMode.WORDS) setConfig(25);
    else if (newMode === TypingMode.QUOTE) setConfig(1); 
    setView('test');
  };

  const handleFinish = async (stats: TypingStats, history: HistoryEntry[]) => {
    setIsTyping(false);
    const newResult: SavedResult = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      stats,
      history,
      mode,
      language,
      config,
      userEmail: user?.email || undefined
    };
    
    if (user) {
      await saveUserResult(user.uid, newResult);
      const updated = await getUserHistory(user.uid);
      setResults(updated);
    } else {
      setResults(prev => [newResult, ...prev]);
    }
  };

  const updateSettings = async (newSettings: UserSettings) => {
    setSettings(newSettings);
    if (user) {
      await saveUserSettings(user.uid, newSettings);
    }
  };

  const toggleFocusMode = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSettings(prev => ({ ...prev, focusMode: !prev.focusMode }));
    }
  };

  const uiVisibleClass = isTyping && settings.focusMode ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100';

  return (
    <div 
      className="min-h-screen flex flex-col items-center p-6 md:p-12 max-w-7xl mx-auto transition-all duration-500 cursor-default"
      onClick={toggleFocusMode}
    >
      <header className={`w-full flex justify-between items-center mb-12 fade-transition ${uiVisibleClass}`}>
        <div className="flex items-center space-x-3 cursor-pointer group" onClick={() => setView('test')}>
          <div className="bg-main p-1.5 rounded-lg group-hover:rotate-12 transition-transform shadow-lg shadow-main/20">
            <Zap size={24} className="text-[var(--bg-color)]" fill="currentColor" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-text">HyperTyper</h1>
        </div>
        
        <nav className="flex space-x-6 text-sub">
          <button onClick={() => setView('test')} title="Typing Test" className={`transition-colors ${view === 'test' ? 'text-main' : 'hover:text-text'}`}>
            <Keyboard size={20} />
          </button>
          <button onClick={() => setView('leaderboard')} title="Leaderboard" className={`transition-colors ${view === 'leaderboard' ? 'text-main' : 'hover:text-text'}`}>
            <Trophy size={20} />
          </button>
          <button onClick={() => setView('history')} title="History" className={`transition-colors ${view === 'history' ? 'text-main' : 'hover:text-text'}`}>
            <List size={20} />
          </button>
          <button onClick={() => setView('settings')} title="Settings" className={`transition-colors ${view === 'settings' ? 'text-main' : 'hover:text-text'}`}>
            <Settings size={20} />
          </button>
          <button onClick={() => setShowAuth(true)} title="Account" className={`transition-colors ${user ? 'text-main' : 'hover:text-text'}`}>
            <User size={20} />
          </button>
        </nav>
      </header>

      {view === 'test' && (
        <div className={`bg-sub/5 backdrop-blur-sm p-1.5 rounded-xl mb-12 flex flex-wrap items-center shadow-xl justify-center gap-2 fade-transition ${uiVisibleClass}`}>
          <div className="relative px-4 border-r border-sub/30" ref={langMenuRef}>
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-2 py-1.5 text-xs uppercase font-bold text-sub hover:text-main transition-colors"
              >
                <Globe size={14} />
                <span>{language}</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-[var(--bg-color)] border border-sub/30 rounded-xl shadow-2xl z-50 overflow-hidden animate-fadeIn">
                  {[Language.EN, Language.RU, Language.KZ].map(lang => (
                    <button 
                      key={lang}
                      onClick={() => { setLanguage(lang); setIsLangMenuOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-3 text-xs uppercase font-bold transition-all ${language === lang ? 'text-main bg-sub/10' : 'text-sub hover:text-text hover:bg-sub/5'}`}
                    >
                      <span>{lang}</span>
                      {language === lang && <Check size={12} />}
                    </button>
                  ))}
                </div>
              )}
          </div>

          <div className="flex space-x-4 px-4 items-center border-r border-sub/30">
              <button onClick={() => handleModeChange(TypingMode.TIME)} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-all ${mode === TypingMode.TIME ? 'text-main bg-sub/10' : 'text-sub hover:text-text'}`}>
                  <Clock size={16} /><span>time</span>
              </button>
              <button onClick={() => handleModeChange(TypingMode.WORDS)} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-all ${mode === TypingMode.WORDS ? 'text-main bg-sub/10' : 'text-sub hover:text-text'}`}>
                  <Hash size={16} /><span>words</span>
              </button>
              <button onClick={() => handleModeChange(TypingMode.QUOTE)} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-all ${mode === TypingMode.QUOTE ? 'text-main bg-sub/10' : 'text-sub hover:text-text'}`}>
                  <QuoteIcon size={16} /><span>quote</span>
              </button>
          </div>

          <div className="flex space-x-2 px-2 min-w-[120px] justify-center">
              {mode === TypingMode.TIME && [15, 30, 60].map(val => (
                  <button key={val} onClick={() => setConfig(val)} className={`px-3 py-1.5 rounded-lg text-sm transition-all ${config === val ? 'text-main' : 'text-sub hover:text-text'}`}>{val}</button>
              ))}
              {mode === TypingMode.WORDS && [10, 25, 50].map(val => (
                  <button key={val} onClick={() => setConfig(val)} className={`px-3 py-1.5 rounded-lg text-sm transition-all ${config === val ? 'text-main' : 'text-sub hover:text-text'}`}>{val}</button>
              ))}
              {mode === TypingMode.QUOTE && ['short', 'medium', 'long'].map((label, idx) => (
                  <button key={idx} onClick={() => setConfig(idx)} className={`px-3 py-1.5 rounded-lg text-sm transition-all ${config === idx ? 'text-main' : 'text-sub hover:text-text'}`}>{label}</button>
              ))}
          </div>
        </div>
      )}

      <main className="w-full flex flex-col items-center justify-center flex-grow">
        {view === 'test' && (
          <TypingEngine 
              mode={mode} config={config} language={language} 
              focusMode={settings.focusMode} onFinish={handleFinish}
              onStartTyping={() => setIsTyping(true)}
          />
        )}
        {view === 'history' && <HistoryView results={results} onClose={() => setView('test')} onClear={() => {}} onDelete={() => {}} />}
        {view === 'settings' && <SettingsView settings={settings} onChange={updateSettings} onClose={() => setView('test')} />}
        {view === 'leaderboard' && <LeaderboardView onClose={() => setView('test')} />}
      </main>

      <footer className={`w-full py-8 text-sub text-xs flex justify-between items-center border-t border-sub/20 mt-20 fade-transition ${uiVisibleClass}`}>
        <div className="flex space-x-6">
            <a href="#" className="hover:text-text">contact</a>
            <a href="#" className="hover:text-text">support</a>
        </div>
        <div className="flex items-center space-x-2 opacity-50">
            <Target size={14} />
            <span>HyperTyper v1.5.0</span>
        </div>
      </footer>

      {showAuth && <AuthModal user={user} onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default App;
